
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { legacyApi, DeliveryNoteSummary } from '../../../services/legacyApi';
import { CompactTable } from '../../../components/ui/CompactTable';
import { useConfirm } from '../../../hooks/use-confirm';

import { Button } from '../../../components/ui/button';
import { Eye, Trash2, Pencil, Download } from 'lucide-react';
import { StandardActions } from '../components/StandardActions';
import { TableFilterBar } from '../../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../../hooks/useTableFilterState';
import { getDeliveryStatusMeta } from '../utils/statuses';
import { useDebouncedFilterValue } from '../../../hooks/useDebouncedFilterValue';

export const LegacyDeliveries: React.FC = () => {
    const PAGE_SIZE = 50;
    const navigate = useNavigate();
    const { confirm } = useConfirm();
    const [deliveries, setDeliveries] = useState<DeliveryNoteSummary[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [debouncedQ, setDebouncedQ] = useState('');
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({
        q: '',
        dateRange: '|',
        status: 'all'
    });
    const [searchValue, setSearchValue] = useDebouncedFilterValue(state.q, (value) => setFilter('q', value));

    const [searchParams] = useSearchParams();
    const soIdFilter = searchParams.get('so_id');

    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedQ(state.q.trim()), 250);
        return () => window.clearTimeout(t);
    }, [state.q]);

    const loadDeliveries = async (reset: boolean = true) => {
        const nextOffset = reset ? 0 : deliveries.length;
        if (reset) setLoading(true);
        else setLoadingMore(true);
        const [start, end] = state.dateRange.split('|');
        try {
            const response = await legacyApi.sales.listDeliveriesPaged({
                so_id: soIdFilter ? Number(soIdFilter) : undefined,
                q: debouncedQ || undefined,
                status_id: state.status !== 'all' ? Number(state.status) : undefined,
                date_from: start || undefined,
                date_to: end || undefined,
                limit: PAGE_SIZE,
                offset: nextOffset,
            });
            setDeliveries((prev) => (reset ? response.items : [...prev, ...response.items]));
            setTotalCount(response.total);
            setHasMore(response.has_more);
        } catch (err) {
            console.error(err);
        } finally {
            if (reset) {
                setLoading(false);
                setHasLoadedOnce(true);
            }
            else setLoadingMore(false);
        }
    };

    useEffect(() => {
        loadDeliveries(true);
    }, [soIdFilter, debouncedQ, state.dateRange, state.status]);

    if (!hasLoadedOnce && loading) return <div className="p-4">Loading Delivery Notes...</div>;

    const handleDelete = async (dnId: number) => {
        const confirmed = await confirm(`Delete Delivery Note #${dnId}?`, {
            description: 'This action cannot be undone.',
            confirmText: 'Delete',
            variant: 'destructive'
        });
        if (!confirmed) return;
        await legacyApi.sales.deleteDelivery(dnId);
        setDeliveries(prev => prev.filter(d => d.dn_id !== dnId));
        setTotalCount(prev => Math.max(prev - 1, 0));
    };

    const columns = [
        { header: "DN Number", accessor: (d: DeliveryNoteSummary) => d.dn_number || `HC/DN/${String(d.dn_id).padStart(4, '0')}`, className: 'font-mono whitespace-nowrap w-32' },
        { header: "Date", accessor: (d: DeliveryNoteSummary) => d.dn_date ? new Date(d.dn_date).toLocaleDateString() : '-', className: 'w-24 whitespace-nowrap' },
        {
            header: "Sale Order",
            accessor: (d: DeliveryNoteSummary) => (
                <Link
                    to={`/legacy/orders/${d.so_id}`}
                    className="text-blue-600 hover:underline font-mono"
                    onClick={e => e.stopPropagation()}
                >
                    HC/SO/{String(d.so_id).padStart(4, '0')}
                </Link>
            ),
            className: 'w-32 whitespace-nowrap'
        },
        { header: "Customer", accessor: (d: DeliveryNoteSummary) => d.customer_name || '-', className: 'min-w-[180px] max-w-[260px] truncate' },
        { header: "DN Ref", accessor: (d: DeliveryNoteSummary) => d.dn_ref || '-', className: 'min-w-[100px] truncate' },
        { header: "PO Ref", accessor: (d: DeliveryNoteSummary) => d.po_ref || '-', className: 'min-w-[100px] truncate' },
        { header: "Location", accessor: (d: DeliveryNoteSummary) => d.delivery_location || '-', className: 'min-w-[150px] max-w-[250px] truncate' },
        { header: "Items", accessor: (d: DeliveryNoteSummary) => d.item_count, className: 'w-16 text-center' },
        {
            header: "Status",
            accessor: (d: DeliveryNoteSummary) => {
                const meta = getDeliveryStatusMeta(Number(d.status_id || 1));
                return (
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${meta.badgeClass}`}>
                        {meta.label}
                    </span>
                );
            },
            className: 'w-28'
        },
        { header: "Created By", accessor: (d: DeliveryNoteSummary) => d.created_by || '-', className: 'w-32 truncate' },
        {
            header: "Actions",
            accessor: (d: DeliveryNoteSummary) => (
                <StandardActions
                    view={{
                        title: "View Details",
                        onClick: (e) => { e.stopPropagation(); navigate(`/legacy/delivery/${d.dn_id}`); },
                        icon: <Eye className="w-4 h-4 text-green-600" />
                    }}
                    edit={{
                        title: "Edit Delivery Note",
                        onClick: (e) => { e.stopPropagation(); navigate(`/legacy/delivery/${d.dn_id}?edit=1`); },
                        icon: <Pencil className="w-4 h-4 text-blue-600" />,
                        disabled: (d.status_id || 1) !== 1
                    }}
                    download={{
                        title: "Download PDF",
                        onClick: async (e) => { e.stopPropagation(); await legacyApi.sales.downloadDeliveryNote(d.dn_id); },
                        icon: <Download className="w-4 h-4 text-purple-600" />
                    }}
                    remove={{
                        title: "Delete Delivery Note",
                        onClick: async (e) => { e.stopPropagation(); await handleDelete(d.dn_id); },
                        icon: <Trash2 className="w-4 h-4 text-red-600" />,
                        disabled: (d.status_id || 1) !== 1
                    }}
                />
            ),
            className: 'text-right'
        }
    ];

    return (
        <div className="h-full flex flex-col space-y-6">
            <div className="flex items-center justify-between space-y-2">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight text-foreground">Delivery Notes</h2>
                    <p className="text-muted-foreground">Manage and view all dispatched delivery notes - showing {deliveries.length} of {totalCount}</p>
                </div>
            </div>
            <div className="space-y-4">
                <div className="flex items-center gap-2">
                    <TableFilterBar
                        searchValue={searchValue}
                        onSearchChange={setSearchValue}
                        searchPlaceholder="Search by DN, customer, reference, PO, location..."
                        controls={[
                            {
                                key: 'dateRange',
                                label: 'Date',
                                type: 'daterange',
                                value: state.dateRange,
                                onChange: (value) => setFilter('dateRange', value)
                            },
                            {
                                key: 'status',
                                label: 'Status',
                                type: 'select',
                                value: state.status,
                                onChange: (value) => setFilter('status', value),
                                options: [
                                    { value: 'all', label: 'All Statuses' },
                                    { value: '1', label: 'Draft' },
                                    { value: '2', label: 'Dispatched' },
                                    { value: '3', label: 'Cancelled' }
                                ]
                            }
                        ]}
                        isDirty={isDirty}
                        onClear={clearAll}
                    />
                </div>
                <div className="flex-1 overflow-auto rounded-md border">
                    <>
                        <CompactTable
                            data={deliveries}
                            columns={columns}
                            keyField="dn_id"
                            loading={loading}
                            onRowClick={(row) => navigate(`/legacy/delivery/${row.dn_id}`)}
                        />
                        {hasMore && (
                            <div className="flex justify-center py-4">
                                <Button variant="ghost" onClick={() => loadDeliveries(false)} disabled={loadingMore}>
                                    {loadingMore ? 'Loading...' : 'Load older delivery notes'}
                                </Button>
                            </div>
                        )}
                    </>
                </div>
            </div>
        </div>
    );
};
